import React, { useEffect, useCallback, useMemo } from 'react';
import { Content, Page, Header, HeaderLabel, ErrorPanel, TabbedLayout } from '@backstage/core-components';
import { Table, TableColumn } from '@backstage/core-components';
import { Link } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import { CombinedReleaseHistory } from '@internal/backstage-plugin-ewii-azure';

// Hooks
import { useReleaseData, useReleaseHistory, useFilters, WarningFilter, SummaryFilter } from './hooks';

// Components
import { VersionCell, SummaryCards, FilterBar, ChangeLogModal } from './components';

// Utils and constants
import { ServiceRow, ENVIRONMENTS, getReleaseSummary, getHealthSummary, formatDate, getCommitUrl, getBranchUrl, getChangeLogFileName } from './utils';
import { TABLE_OPTIONS } from './constants';

export const ReleaseOverviewPage = () => {
  const { services, environments, catalogEntities, loading, error, refetch } = useReleaseData();
  const { releaseHistory, fetchCustomDateRangeReleases, clearCustomDateRange } = useReleaseHistory();
  const { 
    warningFilter, 
    setWarningFilter, 
    summaryFilter, 
    setSummaryFilter, 
    customDateRange, 
    setCustomDateRange, 
    filteredServices 
  } = useFilters(services, releaseHistory);

  const releaseSummary = getReleaseSummary(releaseHistory);
  const healthSummary = getHealthSummary(services);

  // Auto-fetch when both dates are filled
  useEffect(() => {
    if (customDateRange?.from && customDateRange?.to) {
      const fromDate = new Date(customDateRange.from);
      const toDate = new Date(customDateRange.to);
      
      if (fromDate > toDate) {
        // Handle error state
        return;
      }
      
      fetchCustomDateRangeReleases(fromDate, toDate)
        .catch((error) => {
          console.error('Error fetching custom date range releases:', error);
        });
    }
  }, [customDateRange?.from, customDateRange?.to, fetchCustomDateRangeReleases]);

  const columns: TableColumn<ServiceRow>[] = useMemo(() => [
    { 
      title: 'System', 
      field: 'serviceName',
      cellStyle: { verticalAlign: 'top', paddingTop: '8px' },
      render: (row: ServiceRow) => (
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <Link to={`/catalog/default/system/${row.systemName}`} style={{ fontWeight: 'bold' }}>
            {row.serviceName}
          </Link>
        </div>
      )
    },
    ...ENVIRONMENTS
      .filter(env => environments.map(e => e.toLowerCase()).includes(env))
      .map(env => ({
        title: env.charAt(0).toUpperCase() + env.slice(1),
        field: env,
        cellStyle: { verticalAlign: 'top', paddingTop: '8px' },
        render: (row: ServiceRow) => {
          const cell = row[env];
          if (typeof cell === 'object' && 'version' in cell) {
            return <VersionCell 
              cell={cell} 
              env={env} 
              row={row} 
              isAcceptanceFilterActive={warningFilter === 'acceptance'}
              isProductionFilterActive={warningFilter === 'production'}
              isStagedFilterActive={warningFilter === 'staged'}
              summaryFilter={summaryFilter}
              customDateRange={customDateRange}
              releaseHistory={releaseHistory}
            />;
          }
          return '-';
        },
      })),
  ], [environments, warningFilter, summaryFilter, customDateRange, releaseHistory]);

  const handleSummaryCardClick = useCallback((type: 'recent' | 'health', timePeriod?: '24h' | '7d' | '30d', environment?: string) => {
    if (summaryFilter?.type === type && 
        summaryFilter?.timePeriod === timePeriod && 
        summaryFilter?.environment === environment) {
      setSummaryFilter(null);
    } else {
      setSummaryFilter({ type, timePeriod, environment });
    }
    setWarningFilter('no-filter');
  }, [summaryFilter, setSummaryFilter, setWarningFilter]);

  const handleClearCustomDateRange = useCallback(() => {
    setCustomDateRange(null);
    clearCustomDateRange();
  }, [setCustomDateRange, clearCustomDateRange]);

  const isCardActive = useCallback((type: 'recent' | 'health', timePeriod?: '24h' | '7d' | '30d', environment?: string) => {
    return summaryFilter?.type === type && 
           summaryFilter?.timePeriod === timePeriod && 
           summaryFilter?.environment === environment;
  }, [summaryFilter]);

  // Helper function to create release detail table for a specific environment
  const createReleaseDetailTable = useCallback((environment: string) => {
    const releases = customDateRange 
      ? (releaseHistory[`${environment}_custom`] || [])
      : (releaseHistory[`${environment}_30d`] || []);
    
    const tableTitle = customDateRange 
      ? `${environment.charAt(0).toUpperCase() + environment.slice(1)} Releases (${new Date(customDateRange.from).toLocaleDateString('da-DK')} - ${new Date(customDateRange.to).toLocaleDateString('da-DK')})`
      : `${environment.charAt(0).toUpperCase() + environment.slice(1)} Releases (Last 30 Days)`;
    
    const releaseColumns: TableColumn<CombinedReleaseHistory>[] = [
      {
        title: 'Component',
        field: 'component',
        render: (release: CombinedReleaseHistory) => {
          const entity = catalogEntities.find(e => e.metadata.name === release.component);
          const displayName = entity?.metadata.title || release.component;
          
          return (
            <Link to={`/catalog/default/component/${release.component}`}>
              {displayName}
            </Link>
          );
        },
      },
      {
        title: 'Release Number',
        field: 'releaseNumber',
        render: (release: CombinedReleaseHistory) => (
          <Link to={release.releaseUrl} target="_blank" rel="noopener noreferrer">
            {release.releaseNumber}
          </Link>
        ),
      },
      {
        title: 'Date',
        field: 'releaseDate',
        defaultSort: 'desc',
        render: (release: CombinedReleaseHistory) => 
          release.releaseDate ? formatDate(release.releaseDate.toISOString()) : 'N/A',
      },
      {
        title: 'Status',
        field: 'status',
        render: (release: CombinedReleaseHistory) => (
          <span style={{ 
            color: release.status === 'deployed' ? '#28a745' : 
                   release.status === 'staged' ? '#ffc107' : 
                   release.status === 'cancelled' ? '#dc3545' : '#6c757d',
            fontWeight: 'bold',
            textTransform: 'capitalize'
          }}>
            {release.status}
          </span>
        ),
      },
      {
        title: 'Branch',
        field: 'toBranch',
        render: (release: CombinedReleaseHistory) => {
          const entity = catalogEntities.find(e => e.metadata.name === release.component);
          
          if (entity && release.toCommit) {
            const branchUrl = getBranchUrl(entity, release.toBranch, release.toCommit);
            if (branchUrl) {
              return (
                <Link to={branchUrl} target="_blank" rel="noopener noreferrer">
                  {release.toBranch}
                </Link>
              );
            }
          }
          return release.toBranch;
        },
      },
      {
        title: 'Commit',
        field: 'toCommit',
        render: (release: CombinedReleaseHistory) => {
          const shortCommit = release.toCommit?.substring(0, 8) || 'N/A';
          
          const entity = catalogEntities.find(e => e.metadata.name === release.component);
          
          if (entity && release.toCommit) {
            const commitUrl = getCommitUrl(entity, release);
            if (commitUrl) {
              return (
                <Link to={commitUrl} target="_blank" rel="noopener noreferrer">
                  <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {shortCommit}
                  </span>
                </Link>
              );
            }
          }
          
          return (
            <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
              {shortCommit}
            </span>
          );
        },
      },
      {
        title: 'Change Log',
        field: 'changeLogUrl',
        render: (release: CombinedReleaseHistory) => {
          const fileName = getChangeLogFileName(release.changeLogFile, release.changeLogUrl);
          return (
            <ChangeLogModal 
              component={release.component} 
              fileName={fileName}
              displayText="View"
              fullRowExpansion={true}
            />
          );
        },
      },
    ];

    return (
      <Table
        title={tableTitle}
        options={{
          ...TABLE_OPTIONS,
          searchText: '',
        }}
        columns={releaseColumns}
        data={releases}
        isLoading={loading}
      />
    );
  }, [customDateRange, releaseHistory, catalogEntities, loading]);

  return (
    <Page themeId="home">
      <Header title="Release Overview" subtitle="Overview of service versions per environment">
        <HeaderLabel label="Owner" value="EWII IT" />
      </Header>
      <Content>
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <ErrorPanel
              error={error as Error}
              title="Failed to load service data"
            />
          </div>
        )}
        
        {/* Summary Cards */}
        <SummaryCards
          releaseSummary={releaseSummary}
          healthSummary={healthSummary}
          customDateRange={customDateRange}
          releaseHistory={releaseHistory}
          onCardClick={handleSummaryCardClick}
          isCardActive={isCardActive}
        />

        {/* Filter Bar */}
        <FilterBar
          warningFilter={warningFilter}
          setWarningFilter={setWarningFilter}
          summaryFilter={summaryFilter}
          customDateRange={customDateRange}
          setCustomDateRange={setCustomDateRange}
          onClearCustomDateRange={handleClearCustomDateRange}
        />

        {/* Table highlighting for custom date range */}
        <TabbedLayout>
          <TabbedLayout.Route path="/" title="Overview">
            {error ? (
              <ErrorPanel
                error={error as Error}
                title="Failed to load service data"
              />
            ) : (
              <Table
                title="Services"
                options={TABLE_OPTIONS}
                columns={columns}
                data={loading ? [] : filteredServices}
                isLoading={loading}
              />
            )}
          </TabbedLayout.Route>
          <TabbedLayout.Route path="/test" title="Test Releases">
            {error ? (
              <ErrorPanel
                error={error as Error}
                title="Failed to load release data"
              />
            ) : (
              createReleaseDetailTable('test')
            )}
          </TabbedLayout.Route>
          <TabbedLayout.Route path="/acceptance" title="Acceptance Releases">
            {error ? (
              <ErrorPanel
                error={error as Error}
                title="Failed to load release data"
              />
            ) : (
              createReleaseDetailTable('acceptance')
            )}
          </TabbedLayout.Route>
          <TabbedLayout.Route path="/production" title="Production Releases">
            {error ? (
              <ErrorPanel
                error={error as Error}
                title="Failed to load release data"
              />
            ) : (
              createReleaseDetailTable('production')
            )}
          </TabbedLayout.Route>
        </TabbedLayout>
      </Content>
    </Page>
  );
};

export default ReleaseOverviewPage;
